import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ApolloError, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { toast } from 'sonner'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { PageContainer } from '@/components/shared/page-container'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useLanguage } from '@/context/LanguageContext'
import { useUnsavedChanges } from '@/context/UnsavedChangesContext'
import ConfirmationDialog from '@/components/ConfirmationDialog'
import {
  DELETE_SINGLE_PAGE,
  GET_PAGE,
  GET_PAGES_TREE,
  PAGE_DELETE_INFO,
  UPDATE_PAGE,
} from '../pageQueries'
import { pageFormSchema } from '../pageFormSchema'
import { buildPageProfiles } from '../pageProfiles'
import { buildPagePublicUrl, PageTreeNodeForUrl } from '../pagePublicUrl'
import { PageFormValues } from '../types'
import { ContentBlockItem } from '@/containers/ContentBlocks/components/DraggableContentBlockItem'
import { RemoveBlockDialog } from '@/containers/ContentBlocks/components/RemoveBlockDialog'
import {
  CREATE_CONTENT_BLOCK,
  DELETE_MANY_CONTENT_BLOCKS,
  UPDATE_CONTENT_BLOCK,
  UPDATE_LINKED_CONTENT_BLOCK,
  UPDATE_CONTENT_BLOCK_PROFILE,
} from '@/containers/ContentBlocks/contentBlockQueries'
import {
  buildContentBlockVariables,
  contentBlockFormSchema,
  describeContentBlockValidationError,
  isLinkedContentBlockType,
  overlayProfileNames,
} from '@/containers/ContentBlocks/contentBlockPayload'
import { ContentBlockFormValues } from '@/containers/ContentBlocks/types'
import PageSettingsPanel from './PageSettingsPanel'
import PageContentBlocksPanel from './PageContentBlocksPanel'
import BlockEditPanel from './BlockEditPanel'
import { DeletePageDialog, PageDeleteInfo } from './DeletePageDialog'
import { PageProfile } from '../types'
import {
  BlockDraft,
  stageDescription as withStagedDescription,
  stageFormValues as withStagedFormValues,
  stageName as withStagedName,
} from '../blockDraft'

// Same env var + fallback convention as `useImpersonateB2BPortal` (this app
// uses `REACT_APP_` + `process.env`, not `VITE_`/`import.meta.env` — see
// vite.config.ts's `define: { "process.env": process.env }`).
const B2B_PORTAL_URL =
  process.env.REACT_APP_B2B_PORTAL_URL || 'http://localhost:8090'

function getBlockName(block: ContentBlockItem): string {
  const profile = block.content_block_profiles.find(p => p.language === 'en')
  return (
    profile?.name ||
    block.content_block_profiles[0]?.name ||
    `Block #${block.id}`
  )
}

function seedBlocks(rawBlocks: ContentBlockItem[] | undefined): ContentBlockItem[] {
  if (!rawBlocks) return []
  return rawBlocks.map((block, index) => ({
    ...block,
    arrangement: block.arrangement ?? index,
  }))
}

/**
 * Build the form's `page_profiles` array in `allLanguages` order from the
 * server's profiles. Languages with a matching server profile carry its `id`
 * (so a subsequent save takes the update path); languages with no match get an
 * id-less blank profile.
 *
 * Shared by the initial load effect AND the post-save reset so the two can't
 * drift.
 */
function buildFormProfiles(
  serverProfiles: PageProfile[] | undefined,
  allLanguages: string[]
): PageProfile[] {
  const existingProfiles =
    serverProfiles?.filter(p => p.language && allLanguages.includes(p.language)) ??
    []

  return allLanguages.map(lang => {
    const existingProfile = existingProfiles.find(p => p.language === lang)
    if (existingProfile) {
      return {
        id: existingProfile.id,
        language: existingProfile.language,
        slug: existingProfile.slug ?? '',
        title: existingProfile.title ?? '',
        subtitle: existingProfile.subtitle ?? '',
        meta_description: existingProfile.meta_description ?? '',
        cta_label: existingProfile.cta_label ?? '',
        cta_link: existingProfile.cta_link ?? '',
      }
    }
    return {
      language: lang,
      slug: '',
      title: '',
      subtitle: '',
      meta_description: '',
      cta_label: '',
      cta_link: '',
    }
  })
}

export default function PageDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { allLanguages, languageId, language } = useLanguage()

  const [contentBlocks, setContentBlocks] = useState<ContentBlockItem[]>([])
  const [savedBlockIds, setSavedBlockIds] = useState<number[]>([])
  // Staged (unsaved) content-block edits, keyed by block id. Populated by the
  // settings form (BlockEditPanel) and the inline title/description editors
  // (full-size preview); flushed through the SAME existing per-block
  // mutations only when the page-level Save runs — see `onSave` below.
  const [blockDrafts, setBlockDrafts] = useState<Record<number, BlockDraft>>({})
  // Which block (if any) is open in the right-column editor. `null` shows the
  // page settings; `'new'` shows an empty create form; a number edits that block.
  const [selectedBlockId, setSelectedBlockId] = useState<number | 'new' | null>(
    null
  )
  const [backConfirmOpen, setBackConfirmOpen] = useState(false)
  // Delete-page flow: fetch PAGE_DELETE_INFO first, THEN open the dialog with
  // it (rather than opening immediately and loading inside the dialog) — see
  // `handleDeleteClick` below.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletePageInfo, setDeletePageInfo] = useState<PageDeleteInfo | null>(
    null
  )
  // Remove-block flow (shared by the left panel's hover trash AND the right
  // panel's Delete button). Lifted here because PageDetails owns
  // `contentBlocks` + renders both panels, so a single dialog serves both
  // without double-rendering conflicting `RemoveBlockDialog` state.
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [blockToRemove, setBlockToRemove] = useState<ContentBlockItem | null>(
    null
  )
  const [deleteContentBlocks] = useMutation(DELETE_MANY_CONTENT_BLOCKS)
  // "Create New +" — fire CREATE_CONTENT_BLOCK immediately with defaults (no
  // form), then append + select via handleBlockCreated so it renders in the
  // preview right away. `isCreating` disables the button to block double-clicks.
  const [createContentBlock, { loading: isCreating }] =
    useMutation(CREATE_CONTENT_BLOCK)
  // Flush mutations for staged block drafts (see `flushBlockDraft` below) — the
  // SAME mutations the settings form / inline editors used to fire immediately.
  const [updateContentBlock] = useMutation(UPDATE_CONTENT_BLOCK)
  const [updateLinkedContentBlock] = useMutation(UPDATE_LINKED_CONTENT_BLOCK)
  const [updateContentBlockProfile] = useMutation(UPDATE_CONTENT_BLOCK_PROFILE)
  // Hydrate the form/blocks from server data exactly once per page id. Inline
  // content-block saves refetch ContentBlock queries, which updates the shared
  // Apollo cache and gives `pageData` a new identity; without this guard the
  // load effect would re-run and clobber the user's unsaved reorder/add/remove.
  const hydratedId = useRef<string | null>(null)
  // Separate once-guard for the per-language `page_profiles` phase (see the
  // two-phase load effect below). `LanguageProvider` remounts on every route
  // change and starts with `allLanguages = []` until a network round trip
  // resolves it (see LanguageContext.tsx) — most commonly right after
  // navigating here from a fresh page create. Gating the WHOLE hydration on
  // `allLanguages` (as a single effect used to) meant the entire form —
  // including plain fields like `code` that have nothing to do with
  // languages — stayed empty for that whole window, tripping the "Code is
  // required" / "At least one language must have title and slug filled"
  // validation errors on a page that actually has data. Splitting into two
  // phases with two independent guards lets the profiles phase run late
  // (once `allLanguages` shows up) without re-running, or being blocked by,
  // the base-fields phase.
  const hydratedProfilesId = useRef<string | null>(null)

  const { data: { findUniquePage: pageData } = {}, loading } = useQuery(
    GET_PAGE,
    {
      variables: { id },
      fetchPolicy: 'network-only',
      skip: !id,
    }
  )

  // Ancestor chain for the public-URL line below. Reused (not refetched) from
  // whatever `PagesTree` (the /pages list) already put in the Apollo cache
  // for this exact query — default `cache-first` policy, so the common
  // "list -> click a row -> detail" flow costs zero extra network calls; a
  // direct deep link still fetches once. `PagesTree` itself uses
  // `cache-and-network` for freshness on its own screen, which this read
  // doesn't disturb.
  const { data: pagesTreeData } = useQuery(GET_PAGES_TREE)

  const form = useForm<PageFormValues>({
    resolver: zodResolver(pageFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      published: false,
      chromeless: false,
      private: false,
      arrangement: 0,
      hero_imageId: null,
      hero_display: 'full_width',
      hero_height_value: null,
      hero_height_unit: 'vh',
      hero_video_link: null,
      parentId: null,
      page_profiles: [],
      tag_ids: [],
      cta_new_tab: false,
      cta_link_page_id: null,
      cta_link_type: null,
      cta_link_category_id: null,
    },
  })

  const [updatePage, { loading: saving }] = useMutation(UPDATE_PAGE, {
    refetchQueries: ['GET_PAGES', 'GET_PAGES_TREE', 'GetPage'],
  })

  // Fetched on Delete-page click and awaited directly (mirrors
  // `SofaFormEditForm`'s `fetchUsageCount` warn-with-count pattern) rather
  // than via `onCompleted`, so the dialog only opens once the counts are in.
  const [fetchDeleteInfo, { loading: loadingDeleteInfo }] = useLazyQuery(
    PAGE_DELETE_INFO,
    { fetchPolicy: 'network-only' }
  )

  // Does NOT refetch `GetPage` — the page (and its id) is gone after this
  // succeeds, so `onSave`'s refetch-avoidance reasoning doesn't apply here;
  // there's simply nothing left to refetch it FOR.
  const [deletePage, { loading: deletingPage }] = useMutation(
    DELETE_SINGLE_PAGE,
    { refetchQueries: ['GET_PAGES', 'GET_PAGES_TREE'] }
  )

  // --- Load page data into the form + block state (once per page id) ---
  // PHASE A: non-language fields + content blocks. Hydrates as soon as
  // `pageData` arrives — deliberately NOT gated on `allLanguages`, so a slow
  // language fetch (see the `hydratedProfilesId` comment above) never leaves
  // `code`/`arrangement`/`published`/etc. blank on a page that actually has
  // data. If `allLanguages` already happens to be ready at this point (the
  // common case once the languages query is warm in the Apollo cache), the
  // per-language profiles hydrate here too and PHASE B below is a no-op.
  useEffect(() => {
    if (loading || !pageData) return
    if (hydratedId.current === pageData.id) return
    hydratedId.current = pageData.id

    const profilesReady = allLanguages.length > 0
    if (profilesReady) hydratedProfilesId.current = pageData.id

    form.reset({
      code: pageData.code || '',
      published: pageData.published || false,
      chromeless: pageData.chromeless || false,
      private: pageData.private || false,
      arrangement: pageData.arrangement || 0,
      hero_imageId: pageData.hero_imageId ?? null,
      hero_display: pageData.hero_display ?? 'full_width',
      hero_height_value: pageData.hero_height_value ?? null,
      hero_height_unit: pageData.hero_height_unit ?? 'vh',
      hero_video_link: pageData.hero_video_link ?? null,
      parentId: pageData.parentId ?? null,
      // Left empty when `allLanguages` isn't ready yet — PHASE B below fills
      // it in as a targeted follow-up once it is, without re-running this
      // (and without clobbering anything the user edited in between).
      page_profiles: profilesReady
        ? buildFormProfiles(pageData.page_profiles, allLanguages)
        : [],
      tag_ids: pageData.tags?.map((t: { id: number }) => t.id) ?? [],
      cta_new_tab: pageData.cta_new_tab ?? false,
      cta_link_page_id: pageData.cta_link_page_id ?? null,
      cta_link_type: pageData.cta_link_type ?? null,
      cta_link_category_id: pageData.cta_link_category?.id ?? null,
    })

    const seeded = seedBlocks(pageData.content_blocks)
    setContentBlocks(seeded)
    setSavedBlockIds(seeded.map(b => b.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData, loading])

  // PHASE B: per-language `page_profiles`, once `allLanguages` catches up
  // (the common post-create case: PHASE A already ran with `allLanguages`
  // still `[]`, e.g. right after `QuickCreatePageDialog` navigates here on a
  // freshly remounted `LanguageProvider`). Guarded separately from
  // `hydratedId` so a late-arriving `allLanguages` isn't permanently swallowed
  // by PHASE A's once-guard, and ordered behind it (`hydratedId.current ===
  // pageData.id`) so it never runs before PHASE A has established the rest of
  // the form for this page.
  //
  // Uses a targeted `reset(..., { keepDirtyValues: true })` instead of
  // `setValue` — `setValue` alone would NOT update RHF's internal
  // `_defaultValues` baseline (still `[]` from PHASE A), which would make
  // `form.formState.isDirty` flip true the instant languages arrive even
  // though the user hasn't touched anything, wrongly enabling Save and
  // tripping the "unsaved changes" nav guard. Passing `keepDirtyValues: true`
  // re-baselines cleanly while still preserving (not clobbering) any field
  // the user genuinely edited in this window.
  useEffect(() => {
    if (loading || !pageData || allLanguages.length === 0) return
    if (hydratedId.current !== pageData.id) return
    if (hydratedProfilesId.current === pageData.id) return
    hydratedProfilesId.current = pageData.id

    form.reset(
      {
        ...form.getValues(),
        page_profiles: buildFormProfiles(pageData.page_profiles, allLanguages),
      },
      { keepDirtyValues: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData, loading, allLanguages])

  // --- Public URL (B2B portal) ---
  // Deliberately reflects the LIVE (possibly unsaved) slug for the currently
  // selected language rather than the last-saved one: this line previews
  // "what this will resolve to", it must react to the Language switcher, and
  // the Slug field it mirrors (`PageSettingsPanel`, same
  // `page_profiles.${languageId}.slug` path) is itself live/unsaved until
  // Save runs. An unsaved edit can therefore show a URL that 404s until
  // Save — no different from the rest of this form (e.g. `published`) already
  // being edited-but-not-yet-saved.
  const currentSlug = form.watch(`page_profiles.${languageId}.slug`)
  // Same live-value reasoning as `currentSlug` above — used only for the
  // Delete-page dialog's heading, falling back to `code` for a page whose
  // current language has no title filled in.
  const currentTitle = form.watch(`page_profiles.${languageId}.title`)
  const pageDisplayName = currentTitle || pageData?.code || ''

  const publicUrlInfo = useMemo(() => {
    if (!pageData) return null
    return buildPagePublicUrl({
      baseUrl: B2B_PORTAL_URL,
      pageId: pageData.id,
      code: pageData.code,
      published: pageData.published,
      language,
      slug: currentSlug,
      pages: (pagesTreeData?.findManyPage ?? []) as PageTreeNodeForUrl[],
    })
  }, [pageData, language, currentSlug, pagesTreeData])

  // --- Dirty tracking ---
  const blocksDirty = useMemo(
    () =>
      JSON.stringify(contentBlocks.map(b => b.id)) !==
      JSON.stringify(savedBlockIds),
    [contentBlocks, savedBlockIds]
  )
  // Dirty = presence of a draft, not a field-level diff (deliberately simple —
  // see the module doc). This is also what extends the beforeunload / back-nav
  // guards below to cover unsaved content-block edits.
  const hasBlockDrafts = Object.keys(blockDrafts).length > 0
  const dirty = form.formState.isDirty || blocksDirty || hasBlockDrafts

  // Warn on tab close / reload while there are unsaved changes.
  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  // Register this page's dirty state with the app-wide navigation guard (see
  // UnsavedChangesContext) so client-side exits that never fire `beforeunload`
  // — sidebar `Link` navigation, logout — get warned too, via the SAME
  // ConfirmationDialog UX as the in-page "Back to Pages" button below.
  //
  // The cleanup ALWAYS runs `setDirty(false)`: on every dep change (harmless —
  // the effect body immediately re-registers the current `dirty` value) and,
  // critically, on unmount, where there's no re-run to re-register `true`.
  // That guarantees this can never leave a stale `true` registered after
  // PageDetails is gone (which would block navigation app-wide).
  const { setDirty } = useUnsavedChanges()
  useEffect(() => {
    setDirty(dirty)
    return () => setDirty(false)
  }, [dirty, setDirty])

  // --- Flush ONE staged content-block draft through the existing per-block
  // mutations (the SAME mutations the settings form / inline editors used to
  // fire immediately, pre-refactor) ---
  const flushBlockDraft = async (
    block: ContentBlockItem,
    draft: BlockDraft
  ): Promise<{
    ok: boolean
    block: ContentBlockItem
    draft: BlockDraft
    /**
     * Set when `ok` is false BECAUSE the staged draft failed
     * `contentBlockFormSchema.safeParse` (as opposed to a mutation/network
     * failure, which has no specific reason) — a short, human-readable
     * sentence naming what's wrong, surfaced alongside the block's name in
     * `onSave`'s save-what-works failure toast.
     */
    invalidReason?: string
  }> => {
    let workingBlock = block
    // The draft to keep staged if this flush only PARTIALLY succeeds — see
    // the reconciliation notes below. Only relevant when `ok` ends up false;
    // a fully successful flush has its draft deleted by the caller.
    let workingDraft = draft
    let ok = true
    let invalidReason: string | undefined

    // 1. Settings-form fields (type/style/images/video/cta/grid config/title
    //    alignment/…) — only if the settings form was touched this session.
    //    Folds any inline title override on top of the snapshot so an inline
    //    edit made AFTER the last settings-form snapshot is never lost. This
    //    settings mutation becomes the SOLE writer of `name` whenever
    //    `formValues` is present (see step 2's guard) — the one deliberate
    //    trade-off: if the SAME language's name was edited via BOTH the
    //    inline title editor and the settings form in one uncommitted
    //    session, the inline edit always wins, even if the settings-form
    //    edit happened later (see the task report).
    if (draft.formValues) {
      const overlaidProfiles = overlayProfileNames(
        draft.formValues.content_block_profiles,
        draft.nameByLanguage
      )
      const valuesToValidate: ContentBlockFormValues = {
        ...draft.formValues,
        content_block_profiles: overlaidProfiles,
      }
      // Staged drafts hold RAW, unfiltered, index-aligned form values (see
      // `ContentBlockFormBody`'s staging effect) — validate + filter them
      // HERE, at flush time, via the SAME schema the settings form's own
      // resolver uses. A failed parse is an ordinary per-block flush
      // failure (save-what-works): the draft stays staged as-is (nothing
      // sent, nothing lost) and is reported by name + reason below, exactly
      // like a mutation/network failure — never a raw `ZodError` dump.
      const parsed = contentBlockFormSchema.safeParse(valuesToValidate)
      if (!parsed.success) {
        ok = false
        invalidReason = describeContentBlockValidationError(parsed.error)
      } else {
        try {
          const isLinkedType = isLinkedContentBlockType(parsed.data.content_block_type)
          // Deliberately INSIDE the try: `buildContentBlockVariables` can
          // throw (malformed `extra_css` JSON — normally already caught by
          // the `safeParse` above, but this stays defensive) and must be
          // treated as an ordinary per-block flush failure, not escape and
          // abort the whole page save (see PageDetails.onSave / Promise.all).
          const variables = buildContentBlockVariables(parsed.data, {
            hideDescription: true,
            rawConfig: (block.config as Record<string, unknown>) ?? {},
            // This flush path only ever updates an existing block (it always
            // targets `block.id`) — never a create.
            isCreate: false,
          })
          const mutate = isLinkedType ? updateLinkedContentBlock : updateContentBlock
          const { data } = await mutate({
            variables: { where: { id: block.id }, ...variables },
          })
          const updatedResult = data?.updateExistingContentBlock
          if (updatedResult) {
            workingBlock = { ...workingBlock, ...updatedResult }
            // Re-derive the staged draft's profile ids from what the server
            // just created/updated, so a RETRY (e.g. triggered by step 2
            // below failing) sends the update path instead of replaying this
            // same id-less create — the backend's id-less branch creates
            // unconditionally, so replaying it duplicates the profile row.
            const serverProfiles: { id: number; language: string }[] =
              updatedResult.content_block_profiles ?? []
            workingDraft = {
              ...workingDraft,
              formValues: {
                ...draft.formValues,
                content_block_profiles: draft.formValues.content_block_profiles.map(p => {
                  const serverProfile = serverProfiles.find(sp => sp.language === p.language)
                  return serverProfile ? { ...p, id: serverProfile.id } : p
                }),
              },
            }
          }
        } catch {
          ok = false
        }
      }
    }

    // 2. Inline name/description overrides, via UPDATE_CONTENT_BLOCK_PROFILE —
    //    the same mutation `persistDescription`/`handleSaveTitle` used to fire
    //    directly (preserving its update-vs-create-profile branching). `name`
    //    is only sent here when the settings form was NEVER touched this
    //    session; otherwise step 1 already owns it (with the overlay above),
    //    so sending it again here would be a second, conflicting write.
    const languagesToFlush = new Set<string>([
      ...(draft.formValues ? [] : Object.keys(draft.nameByLanguage)),
      ...Object.keys(draft.descriptionByLanguage),
    ])

    for (const lang of languagesToFlush) {
      const nameOverride = draft.formValues ? undefined : draft.nameByLanguage[lang]
      const descriptionOverride = draft.descriptionByLanguage[lang]
      if (nameOverride === undefined && descriptionOverride === undefined) continue

      // A synthetic `id: -1` (set by the inline editors' optimistic local
      // merge — see DraggableContentBlockItem) marks a profile that doesn't
      // exist on the server yet; real ids are always positive.
      const existingProfile = workingBlock.content_block_profiles.find(
        p => p.language === lang && p.id > 0
      )

      try {
        let data: any
        if (existingProfile) {
          const res = await updateContentBlockProfile({
            variables: {
              where: { id: block.id },
              data: {
                content_block_profiles: {
                  update: [
                    {
                      where: { id: existingProfile.id },
                      data: {
                        ...(nameOverride !== undefined && {
                          name: { set: nameOverride },
                        }),
                        ...(descriptionOverride !== undefined && {
                          description: { set: descriptionOverride },
                          description_format: { set: 'markdown' },
                        }),
                      },
                    },
                  ],
                },
              },
            },
          })
          data = res.data
        } else {
          // Create path — no profile for this language yet. name is
          // required; borrow it from the primary (English / first REAL)
          // profile when this session didn't stage a name for it.
          const sourceProfile =
            workingBlock.content_block_profiles.find(
              p => p.language === 'en' && p.id > 0
            ) || workingBlock.content_block_profiles.find(p => p.id > 0)
          const res = await updateContentBlockProfile({
            variables: {
              where: { id: block.id },
              data: {
                content_block_profiles: {
                  create: [
                    {
                      language: lang,
                      name: nameOverride ?? sourceProfile?.name ?? '',
                      ...(descriptionOverride !== undefined && {
                        description: descriptionOverride,
                        description_format: 'markdown',
                      }),
                    },
                  ],
                },
              },
            },
          })
          data = res.data
        }

        const updatedResult = data?.updateOneContentBlock
        if (updatedResult) {
          workingBlock = {
            ...workingBlock,
            content_block_profiles: updatedResult.content_block_profiles,
          }
          // Same retry-idempotency reconciliation as step 1: if this step
          // just created a profile for `lang` and the settings-form snapshot
          // ALSO carries an id-less entry for that language, sync its id too
          // so a later retry of step 1 doesn't recreate it.
          if (workingDraft.formValues) {
            const serverProfile = (
              updatedResult.content_block_profiles as { id: number; language: string }[]
            ).find(sp => sp.language === lang)
            if (serverProfile) {
              const formValuesForUpdate = workingDraft.formValues
              workingDraft = {
                ...workingDraft,
                formValues: {
                  ...formValuesForUpdate,
                  content_block_profiles: formValuesForUpdate.content_block_profiles.map(p =>
                    p.language === lang ? { ...p, id: serverProfile.id } : p
                  ),
                },
              }
            }
          }
        }
      } catch {
        ok = false
      }
    }

    return { ok, block: workingBlock, draft: workingDraft, invalidReason }
  }

  // --- Save ---
  // `onSave` is recreated every render, so `values` and `contentBlocks` are
  // current. We reset dirty state explicitly here (rather than relying on the
  // GetPage refetch to reseed, which the hydrate-once guard now prevents).
  const onSave = async (values: PageFormValues) => {
    if (!id) return

    // Flush staged content-block drafts FIRST — save-what-works: blocks that
    // succeed clear from the draft and refresh the local preview from the
    // mutation response; blocks that fail (mutation/network error, OR the
    // staged draft still failing `contentBlockFormSchema` — see
    // `flushBlockDraft`) REMAIN staged (nothing is lost) and are reported by
    // name (+ reason, when known). The page fields below still save
    // regardless — there is no whole-save refusal here: every edit is always
    // staged (see `ContentBlockFormBody`'s staging effect), so there's never
    // an in-progress, un-stageable edit sitting outside `blockDrafts` that a
    // save could silently drop.
    const draftEntries = Object.entries(blockDrafts)
    if (draftEntries.length > 0) {
      const results = await Promise.all(
        draftEntries.map(async ([blockIdStr, draft]) => {
          const blockId = Number(blockIdStr)
          const block = contentBlocks.find(b => b.id === blockId)
          // The block was removed from the page since it was staged (should
          // already be cleared by clearBlockDraft, but guard defensively) —
          // nothing left to flush against.
          if (!block)
            return {
              blockId,
              ok: true,
              block: null as ContentBlockItem | null,
              draft,
              invalidReason: undefined as string | undefined,
            }
          const {
            ok,
            block: updatedBlock,
            draft: updatedDraft,
            invalidReason,
          } = await flushBlockDraft(block, draft)
          return {
            blockId,
            ok,
            block: updatedBlock as ContentBlockItem | null,
            draft: updatedDraft,
            invalidReason,
          }
        })
      )

      const failed = results.filter(r => !r.ok)

      setBlockDrafts(prev => {
        const next = { ...prev }
        for (const r of results) {
          if (r.ok) {
            delete next[r.blockId]
          } else {
            // Keep the draft staged (nothing lost) but RECONCILED with any
            // ids a partially-successful flush just created — see
            // `flushBlockDraft`'s retry-idempotency notes. Without this, a
            // retry would resend the same id-less profile and the backend's
            // unconditional create branch would duplicate it.
            next[r.blockId] = r.draft
          }
        }
        return next
      })
      setContentBlocks(prev =>
        prev.map(b => {
          const r = results.find(res => res.blockId === b.id)
          // Apply the returned block whenever a flush touched it, even on
          // partial failure — the sub-steps that DID succeed are real server
          // state and must not be left stale locally (that staleness is what
          // feeds the duplicate-profile retry hazard above).
          return r?.block ? r.block : b
        })
      )

      if (failed.length > 0) {
        const parts = failed.map(r => {
          const b = contentBlocks.find(cb => cb.id === r.blockId)
          const name = b ? getBlockName(b) : `Block #${r.blockId}`
          return r.invalidReason ? `${name} (${r.invalidReason})` : name
        })
        toast.error(
          `Failed to save changes for: ${parts.join(', ')}. They remain unsaved — please try again.`
        )
      }
    }

    const ids = contentBlocks.map(b => b.id)
    // Capture at call time to avoid stale-closure bugs in the async path.
    const languagesAtSave = allLanguages
    try {
      const { data } = await updatePage({
        variables: {
          where: { id },
          code: values.code,
          published: values.published,
          chromeless: values.chromeless,
          private: values.private,
          arrangement: values.arrangement,
          hero_imageId: values.hero_imageId,
          hero_display: values.hero_display,
          hero_height_value: values.hero_height_value ?? null,
          hero_height_unit: values.hero_height_unit ?? null,
          hero_video_link: values.hero_video_link ?? null,
          parentId: values.parentId ?? null,
          page_profiles: buildPageProfiles(values.page_profiles),
          content_block_ids: ids,
          tag_ids: values.tag_ids ?? [],
          cta: {
            new_tab: values.cta_new_tab ?? false,
            link_page_id: values.cta_link_page_id || null,
            // Legacy rows saved before `cta_link_type` existed only have
            // `cta_link_page_id` set — fall back to `cms_page` so an
            // untouched save of a pre-existing CMS-page CTA doesn't send
            // `link_type: null` (which would disconnect `cta_link_page`).
            // Mirrors the portal's own `resolveCtaHref` legacy fallback.
            link_type:
              values.cta_link_type ??
              (values.cta_link_page_id ? 'cms_page' : null),
            link_category_id: values.cta_link_category_id ?? null,
          },
        },
      })

      const updated = data?.updateExistingPage

      // Reseed `page_profiles` from the RESPONSE so auto-filled (id-less)
      // languages now carry their server `id`; a subsequent save then takes the
      // update path instead of re-creating duplicate profiles. The settings
      // fields come from the submitted `values` (the response selection omits
      // some of them, e.g. parentId). This manual reset replaces the GetPage
      // refetch that the hydrate-once guard prevents.
      //
      // Spread `form.getValues()` FIRST (before `...values`) so any field
      // registered on the form but NOT part of `pageFormSchema` — e.g.
      // `CtaEditor`'s `__cta_editor_unused_collection_id` scratch field,
      // registered whenever a caller omits `linkCollectionIdName` (see
      // `CtaEditor/index.tsx`) — survives into the new baseline. Without
      // this, that key exists in the live form (registered) but not in the
      // reset payload, so `_defaultValues` ends up missing it while
      // `_formValues` keeps it; react-hook-form's dirty check is a
      // `Object.keys().length`-sensitive deep-equal, so that one missing key
      // alone makes `formState.isDirty` read `true` on every render after
      // any save, forever, with `dirtyFields` staying empty (nothing
      // field-level to point at). `...values` (the zod-validated submission)
      // still wins for every real schema field since it's spread after.
      //
      // Note: the SAME key is briefly missing again right after a fresh
      // mount (Phase A's reset below doesn't carry it either) — that's
      // expected and harmless, since `isDirty` is only actually recomputed
      // when the next form event fires, and by then the FIRST save has
      // already rebuilt the baseline with the key present.
      form.reset({
        ...form.getValues(),
        ...values,
        page_profiles: buildFormProfiles(updated?.page_profiles, languagesAtSave),
        tag_ids: values.tag_ids ?? [],
      })

      // Do NOT overwrite local `contentBlocks` from the response. The local
      // state is already current (inline edits + add/remove/reorder all live
      // there) and carries the full preview fields; the UPDATE_PAGE response now
      // returns content_blocks as ids only (it intentionally omits the
      // non-nullable `product_containers`/`categories` relations that the page
      // resolver doesn't populate, which would otherwise fail serialization).
      // Reset the dirty baseline from the ids we just saved so `blocksDirty`
      // clears and the Save button disables.
      setSavedBlockIds(ids)

      toast.success('Page updated successfully')
    } catch {
      toast.error('Failed to update page')
    }
  }

  // --- Block create / update (from the right-column BlockEditPanel) ---
  // These own `contentBlocks`, so the merge logic lives here rather than in the
  // left panel. The block form emits the FULL block (preview fields included),
  // so the left-column full-size preview refreshes without a page refetch.
  const handleBlockCreated = (block: ContentBlockItem) => {
    setContentBlocks(prev => [...prev, { ...block, arrangement: prev.length }])
    // Stay in the editor on the freshly created block. This flips blocksDirty
    // true — correct, the new block still needs UPDATE_PAGE to link it.
    setSelectedBlockId(block.id)
  }

  // --- Create a new block immediately with defaults (no form) ---
  // Fires CREATE_CONTENT_BLOCK with the same default variable shape that
  // ContentBlockFormBody.onSubmit sends for a new `only_text` block, then routes
  // the result through handleBlockCreated (append + select). The block renders in
  // the left preview right away showing the inline "Click to add title/description"
  // placeholders; the page Save persists the attachment (UPDATE_PAGE).
  const handleCreateNewBlock = async () => {
    if (isCreating) return
    try {
      const { data } = await createContentBlock({
        variables: {
          content_block_profiles: [
            { name: '', description: '', link: null, language },
          ],
          content_block_type: 'only_text',
          style: '1_column_title_top',
          images: [],
          gallery_images: [],
          video_link: null,
          video_type: null,
          video_autoplay: false,
          video_loop: false,
          default_margins: true,
          object_fit_cover: false,
          extra_css: null,
          text_color: null,
          background_color: null,
        },
      })
      const created = data?.createNewContentBlock
      if (!created) {
        toast.error('Failed to create content block')
        return
      }
      // Normalize to ContentBlockItem (mirrors ContentBlockFormBody's toBlockItem):
      // the mutation result is typed `any`, so coerce style/arrangement explicitly.
      handleBlockCreated({
        ...created,
        style: created.style || null,
        arrangement: created.arrangement ?? null,
      })
      toast.success('Content block created')
    } catch {
      toast.error('Failed to create content block')
    }
  }

  const handleBlockUpdated = (block: ContentBlockItem) => {
    setContentBlocks(prev =>
      prev.map(b =>
        b.id === block.id ? { ...b, ...block, arrangement: b.arrangement } : b
      )
    )
    // Stay on the block (do not auto-return). Id unchanged → blocksDirty stays
    // as-is.
  }

  // --- Stage content-block edits (no server write until the page Save) ---
  // The settings form (BlockEditPanel → ContentBlockFormBody) and the inline
  // title/description editors (full-size preview) all funnel into this SAME
  // per-block draft map. `onSave` flushes it through the existing per-block
  // mutations — see the flush helpers below.
  const handleStageFormValues = (
    blockId: number,
    values: ContentBlockFormValues,
    seededNameByLanguage: Record<string, string>
  ) => {
    setBlockDrafts(prev => {
      const staged = withStagedFormValues(prev[blockId], values)
      // If the settings form's name for a language now DIFFERS from what
      // THAT FORM WAS SEEDED WITH, the user just typed something new into
      // the (visible, focusable) settings Title field — drop the stale
      // inline-title override for that language so the flush-time overlay
      // (`overlayProfileNames`, applied by both this form's seed and
      // `flushBlockDraft`) doesn't silently rewrite what was just typed back
      // to the old inline value (Defect C).
      //
      // Compare against the SEED, not against the override itself. The seed
      // already folds the override on top (`ContentBlockFormBody`'s
      // `nameOverrides`) — but only for overrides that existed AT THE TIME
      // this form was mounted. `nameOverrides` is deliberately excluded from
      // that form's population-effect deps (so it can't reset the form
      // mid-edit), so an inline edit staged AFTER the settings panel was
      // already open never reaches its Title field. Comparing to the override
      // would then read as "the settings form disagrees with the override" —
      // even though the user never touched Title here — and wrongly discard
      // the (newer) inline edit instead of the (unrelated) settings change
      // that actually happened. Comparing to the seed instead only fires when
      // the visible Title field's value genuinely changed under the user's
      // hands in THIS form.
      const nameByLanguage = { ...staged.nameByLanguage }
      for (const profile of values.content_block_profiles) {
        if (nameByLanguage[profile.language] === undefined) continue
        const seededName = seededNameByLanguage[profile.language] ?? ''
        if ((profile.name ?? '') !== seededName) {
          delete nameByLanguage[profile.language]
        }
      }
      return { ...prev, [blockId]: { ...staged, nameByLanguage } }
    })
  }

  const handleStageName = (blockId: number, language: string, value: string) => {
    setBlockDrafts(prev => ({ ...prev, [blockId]: withStagedName(prev[blockId], language, value) }))
  }

  const handleStageDescription = (blockId: number, language: string, value: string) => {
    setBlockDrafts(prev => ({
      ...prev,
      [blockId]: withStagedDescription(prev[blockId], language, value),
    }))
  }

  // --- Remove a block (open the choice dialog) ---
  // Triggered from the left panel's hover trash (thumbnail/non-editor path is
  // gone in full-size, so this is the right panel's Delete button + any future
  // caller). Opens RemoveBlockDialog; the actual removal happens in the two
  // handlers below.
  const handleRequestDelete = (blockId: number) => {
    const block = contentBlocks.find(b => b.id === blockId)
    if (block) {
      setBlockToRemove(block)
      setRemoveDialogOpen(true)
    }
  }

  // Drop any staged draft for a block that's leaving `contentBlocks` — nothing
  // left to flush it against.
  const clearBlockDraft = (blockId: number) => {
    setBlockDrafts(prev => {
      if (!(blockId in prev)) return prev
      const next = { ...prev }
      delete next[blockId]
      return next
    })
  }

  const handleRemoveFromPage = () => {
    if (blockToRemove) {
      setContentBlocks(prev => prev.filter(b => b.id !== blockToRemove.id))
      // If the removed block was open in the editor, fall back to settings.
      if (selectedBlockId === blockToRemove.id) setSelectedBlockId(null)
      clearBlockDraft(blockToRemove.id)
      setBlockToRemove(null)
      setRemoveDialogOpen(false)
    }
  }

  const handleDeletePermanently = async () => {
    if (blockToRemove) {
      try {
        await deleteContentBlocks({ variables: { ids: [blockToRemove.id] } })
        setContentBlocks(prev => prev.filter(b => b.id !== blockToRemove.id))
        if (selectedBlockId === blockToRemove.id) setSelectedBlockId(null)
        clearBlockDraft(blockToRemove.id)
        toast.success('Content block deleted permanently')
        setRemoveDialogOpen(false)
      } catch {
        toast.error('Failed to delete content block')
      }
      setBlockToRemove(null)
    }
  }

  // --- Back navigation (guarded) ---
  const handleBack = () => {
    if (dirty) {
      setBackConfirmOpen(true)
    } else {
      navigate('/pages')
    }
  }

  // --- Delete page (fetch counts first, then open the confirm dialog) ---
  const handleDeleteClick = async () => {
    if (!id) return
    // `useLazyQuery`'s execute promise does NOT reject on a GraphQL/network
    // error (unlike `useMutation`'s below) — it resolves with `error` set —
    // so the failure case is read from `result.error`, not a `catch`.
    const result = await fetchDeleteInfo({ variables: { id } })
    const info = result.data?.pageDeleteInfo
    if (!info) {
      toast.error(result.error?.message || 'Failed to load page delete info')
      return
    }
    setDeletePageInfo(info)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDeletePage = async (children?: 'delete' | 'promote') => {
    if (!id) return
    try {
      await deletePage({ variables: { id, children } })
      toast.success('Page deleted')
      setDeleteDialogOpen(false)
      navigate('/pages')
    } catch (error) {
      // Surface the ACTUAL server message — the reserved-code ('home-page' /
      // 'login') and has-children guards live server-side and are the whole
      // point of this toast (mirrors QuickCreatePageDialog's
      // handleCreatePageError).
      const apolloError = error as ApolloError
      const messages: string[] = [
        ...(apolloError?.graphQLErrors?.map(e => e.message) ?? []),
        apolloError?.message,
      ].filter(Boolean)
      toast.error(messages[0] || 'Failed to delete page')
    }
  }

  if (loading && !pageData) {
    return (
      <PageContainer>
        <div className="mb-6 flex items-center justify-between">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-9 w-24" />
        </div>
        <div className="grid grid-cols-3 gap-8">
          <div className="col-span-2">
            <Skeleton className="h-96 w-full" />
          </div>
          <div>
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </PageContainer>
    )
  }

  if (!pageData) {
    return (
      <PageContainer>
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => navigate('/pages')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Pages
          </Button>
          <p className="text-muted-foreground">Page not found.</p>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <div className="mb-6 flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Pages
        </Button>
        <Button
          type="button"
          onClick={form.handleSubmit(onSave, () =>
            toast.error('Please fix the highlighted fields before saving')
          )}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {publicUrlInfo && (
        <div className="mb-6 flex items-center gap-2 text-sm">
          {publicUrlInfo.url === null ? (
            <span className="text-muted-foreground">{publicUrlInfo.reason}</span>
          ) : publicUrlInfo.clickable ? (
            <a
              href={publicUrlInfo.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
            >
              {publicUrlInfo.url}
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
          ) : (
            <>
              <span className="font-mono text-muted-foreground">
                {publicUrlInfo.url}
              </span>
              <span className="text-muted-foreground">
                ({publicUrlInfo.reason})
              </span>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-2">
          <PageContentBlocksPanel
            contentBlocks={contentBlocks}
            setContentBlocks={setContentBlocks}
            selectedBlockId={selectedBlockId}
            onSelectBlock={setSelectedBlockId}
            onDescriptionSaved={handleBlockUpdated}
            onStageDescription={handleStageDescription}
            onStageName={handleStageName}
            onRequestDelete={handleRequestDelete}
            onCreateNewBlock={handleCreateNewBlock}
            isCreating={isCreating}
          />
        </div>
        <div>
          {selectedBlockId === null ? (
            <PageSettingsPanel
              form={form}
              languageId={languageId}
              pageId={id ?? ''}
              heroImageUrl={pageData.hero_image?.src}
              onSave={onSave}
              onDeleteClick={handleDeleteClick}
              deleteInfoLoading={loadingDeleteInfo}
            />
          ) : (
            <BlockEditPanel
              blockId={selectedBlockId}
              onBack={() => setSelectedBlockId(null)}
              onCreated={handleBlockCreated}
              onUpdated={handleBlockUpdated}
              onRequestDelete={handleRequestDelete}
              draft={
                typeof selectedBlockId === 'number'
                  ? blockDrafts[selectedBlockId]?.formValues ?? null
                  : null
              }
              onStageFormValues={handleStageFormValues}
              stagedNameByLanguage={
                typeof selectedBlockId === 'number'
                  ? blockDrafts[selectedBlockId]?.nameByLanguage
                  : undefined
              }
            />
          )}
        </div>
      </div>

      <RemoveBlockDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        blockName={blockToRemove ? getBlockName(blockToRemove) : ''}
        onRemoveFromPage={handleRemoveFromPage}
        onDeletePermanently={handleDeletePermanently}
      />

      <ConfirmationDialog
        open={backConfirmOpen}
        title="Discard unsaved changes?"
        description="You have unsaved changes. Leaving this page will discard them."
        confirmText="Discard & leave"
        cancelText="Stay"
        onCancel={() => setBackConfirmOpen(false)}
        onConfirm={() => {
          setBackConfirmOpen(false)
          navigate('/pages')
        }}
      />

      {deletePageInfo && (
        <DeletePageDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          pageTitle={pageDisplayName}
          info={deletePageInfo}
          deleting={deletingPage}
          onConfirm={handleConfirmDeletePage}
        />
      )}
    </PageContainer>
  )
}
